import { jwtDecode } from "jwt-decode";
import api from "../../../services/axiosConfig";
import { Client } from "@stomp/stompjs";

let stompClient = null;
let activeSubscription = null;

const processInstances = (
  data,
  labelToTitleMap,
  setCardStatus,
  setContainerIds,
  setExposedPorts,
) => {
  const userInstances = data?.user?.instances || data?.instances || [];
  const userCollaborators =
    data?.user?.collaborators || data?.collaborators || [];

  const allInstances = [...userInstances, ...userCollaborators];

  const instances = Array.from(
    new Map(
      allInstances.map((instance) => [instance.containerId, instance]),
    ).values(),
  );

  const newCardStatus = {};
  const newContainerIds = {};
  const newExposedPorts = {};

  const instancesByModule = {};
  instances.forEach((instance) => {
    if (instance?.module?.label) {
      const moduleLabel = instance.module.label;
      if (!instancesByModule[moduleLabel]) {
        instancesByModule[moduleLabel] = [];
      }
      instancesByModule[moduleLabel].push(instance);
    }
  });

  Object.entries(instancesByModule).forEach(
    ([moduleLabel, moduleInstances]) => {
      const title = labelToTitleMap[moduleLabel];
      if (!title || !moduleInstances?.length) return;

      if (moduleLabel === "Fahras") {
        const serverInstance = moduleInstances.find((i) =>
          i.containerName.includes("fahras_server"),
        );
        const ingestionInstance = moduleInstances.find((i) =>
          i.containerName.includes("fahras_ingestion"),
        );

        if (serverInstance && ingestionInstance) {
          if (
            serverInstance.status === "RUNNING" &&
            ingestionInstance.status === "RUNNING"
          ) {
            newCardStatus[title] = "RUNNING";
          } else {
            newCardStatus[title] = "STOPPED";
          }

          newContainerIds[title] = serverInstance.containerId;
          newExposedPorts[title] = serverInstance.exposedPorts;
        } else {
          newCardStatus[title] = "NOT_CREATED";
        }
        return;
      }

      const priorityContainers = {
        Hadoop: ["namenode"],
        Spark: ["master"],
        Hive: ["server"],
        Skeyenote: ["skeyenote"],
        Skeyechart: ["skeyechart", "Skeyechart"],
        Skeyeingest: ["skeyeingest", "Skeyeingest"],
        Skeyeform: ["skeyeform", "Skeyeform"],
        Skeyecube: ["server"],
        Skeyepredict: ["server", "ui"],
        DGHelper: ["dghelper"],
        Jeyeda: ["jeyeda"],
      };

      let mainInstance = null;
      const priorities = priorityContainers[moduleLabel] || [];

      for (const priority of priorities) {
        mainInstance = moduleInstances.find((i) =>
          i.containerName?.includes(priority),
        );
        if (mainInstance) break;
      }

      if (mainInstance && !mainInstance.exposedPorts) {
        const instanceWithPorts = moduleInstances.find(
          (i) => i.exposedPorts && i.exposedPorts.trim() !== "",
        );
        if (instanceWithPorts) mainInstance = instanceWithPorts;
      }

      if (mainInstance) {
        newCardStatus[title] = mainInstance.status;
        newContainerIds[title] = mainInstance.containerId;

        let exposedPortsInstance = null;
        if (mainInstance.exposedPorts) {
          exposedPortsInstance = mainInstance;
        } else {
          exposedPortsInstance = moduleInstances.find(
            (i) => i.exposedPorts && i.exposedPorts.trim() !== "",
          );
        }

        newExposedPorts[title] = exposedPortsInstance
          ? exposedPortsInstance.exposedPorts
          : null;
      }
    },
  );

  setCardStatus(newCardStatus);
  setContainerIds(newContainerIds);
  setExposedPorts(newExposedPorts);
};

const getUsernameFromToken = () => {
  const token = localStorage.getItem("SkeyegateAuthToken");
  if (!token) return null;
  try {
    return jwtDecode(token).sub;
  } catch (e) {
    console.error("Error occurred while decoding JWT token:", e);
    return null;
  }
};

/* const createStompClient = (onStatusChange) => {
  const token = localStorage.getItem("SkeyegateAuthToken");
  const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
  const wsHost = window.location.host; // ← always correct, works everywhere

  return new Client({
    brokerURL: `${wsProtocol}://${wsHost}/api/ws-plain`,
    connectHeaders: { Authorization: token ? `Bearer ${token}` : "" },
    reconnectDelay: 5000,
    onDisconnect: () => onStatusChange && onStatusChange(false),
    onWebSocketClose: () => onStatusChange && onStatusChange(false),
    onWebSocketError: () => onStatusChange && onStatusChange(false),
  });
}; */
const createStompClient = (onStatusChange) => {
  const token = localStorage.getItem("SkeyegateAuthToken");
  const isDev = process.env.NODE_ENV === "development";
  const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
  const wsHost = isDev ? "localhost:8080" : window.location.host;

  return new Client({
    brokerURL: `${wsProtocol}://${wsHost}/api/ws-plain`,
    connectHeaders: { Authorization: token ? `Bearer ${token}` : "" },
    reconnectDelay: 5000,
    onDisconnect: () => onStatusChange && onStatusChange(false),
    onWebSocketClose: () => onStatusChange && onStatusChange(false),
    onWebSocketError: () => onStatusChange && onStatusChange(false),
  });
};

export const disconnectWebSocket = () => {
  if (stompClient?.connected) {
    try {
      stompClient.publish({ destination: "/skeyegate/instances/unsubscribe" });
    } catch (e) {
      console.error("Error occurred while unsubscribing from WebSocket:", e);
    }
    if (activeSubscription) {
      activeSubscription.unsubscribe();
      activeSubscription = null;
    }
    stompClient.deactivate();
  }
  stompClient = null;
};

export const subscribeToInstances = (userId, onMessage, onStatusChange) => {
  disconnectWebSocket();
  stompClient = createStompClient(onStatusChange);

  stompClient.onConnect = () => {
    // FIX 1: Notify the UI that WebSocket connects successfully!
    if (onStatusChange) onStatusChange(true);

    const username = getUsernameFromToken();
    const topicId = username || userId;

    /*     console.info(
      `[WS] Connected – subscribing to full instances for: ${topicId}`,
    ); */

    activeSubscription = stompClient.subscribe(
      `/topic/instances/${topicId}`,
      (message) => {
        try {
          const raw = JSON.parse(message.body);

          const userInstances = raw?.user?.instances || raw?.instances || [];
          const userCollaborators =
            raw?.user?.collaborators || raw?.collaborators || [];

          const allRawInstances = [...userInstances, ...userCollaborators];

          const rawInstances = Array.from(
            new Map(
              allRawInstances.map((instance) => [
                instance.containerId,
                instance,
              ]),
            ).values(),
          );

          const instances = rawInstances.map((instance) => ({
            id: instance.containerId,
            label: instance.module?.label || "Unknown",
            name: instance.containerName,
            status: instance.status,
            moduleId: instance.module?.id,
            cpuUsage:
              instance.cpuUsageValue != null
                ? `${Number(instance.cpuUsageValue).toFixed(2)}%`
                : "N/A",
            cpuUsageValue: instance.cpuUsageValue ?? 0,
            memoryUsage:
              instance.memoryUsageValue != null
                ? `${Number(instance.memoryUsageValue).toFixed(2)} MB`
                : "N/A",
            memoryUsageValue: instance.memoryUsageValue ?? 0,
            diskUsageValue: instance.diskUsageValue ?? 0,
            diskVirtualUsageValue: instance.diskVirtualUsageValue ?? 0,
            diskUsage: instance.diskUsage || "N/A",
            ports: instance.exposedPorts
              ? instance.exposedPorts.split(",").map((p) => p.trim())
              : [],
            isTotal: false,
          }));

          let allData = instances;
          if (instances.length > 0) {
            const totalCpu =
              raw?.totalCpuUsage ??
              instances.reduce((s, i) => s + i.cpuUsageValue, 0);
            const totalMemory =
              raw?.totalMemoryUsage ??
              instances.reduce((s, i) => s + i.memoryUsageValue, 0);
            const totalDisk =
              raw?.totalDiskUsage ??
              instances.reduce((s, i) => s + i.diskUsageValue, 0);
            const totalVirtualDisk =
              raw?.totalVirtualDiskUsage ??
              instances.reduce((s, i) => s + (i.diskVirtualUsageValue || 0), 0);

            const totalDiskDisplay =
              totalVirtualDisk > 0
                ? `${Number(totalDisk).toFixed(2)} GB (virtual ${Number(totalVirtualDisk).toFixed(2)} GB)`
                : "N/A";

            const totalRow = {
              id: "TOTAL_ROW",
              label: "Total Containers Usage",
              name: "Total Usage",
              status: "SUMMARY",
              moduleId: null,
              cpuUsage: `${Number(totalCpu).toFixed(2)}%`,
              cpuUsageValue: totalCpu,
              memoryUsage: `${Number(totalMemory).toFixed(2)} MB`,
              memoryUsageValue: totalMemory,
              diskUsageValue: totalDisk,
              diskUsage: totalDiskDisplay,
              ports: [],
              isTotal: true,
            };

            allData = [totalRow, ...instances];
          }

          if (typeof onMessage === "function") {
            onMessage(allData);
          }
        } catch (err) {
          console.error("[WS] Failed to parse message:", err);
        }
      },
    );

    stompClient.publish({ destination: "/skeyegate/instances/subscribe" });
  };

  stompClient.activate();
  return () => disconnectWebSocket();
};

export const subscribeToInstancesMinimal = (
  userId,
  setCardStatus,
  setContainerIds,
  toast,
  labelToTitleMap,
  setExposedPorts,
  onStatusChange,
) => {
  disconnectWebSocket();

  stompClient = createStompClient(onStatusChange);

  stompClient.onConnect = () => {
    // FIX 1: Notify UI on minimal connection as well!
    if (onStatusChange) onStatusChange(true);

    const username = getUsernameFromToken();
    const topicId = username || userId;

    activeSubscription = stompClient.subscribe(
      `/topic/instances/minimal/${topicId}`,
      (message) => {
        try {
          const data = JSON.parse(message.body);
          processInstances(
            data,
            labelToTitleMap,
            setCardStatus,
            setContainerIds,
            setExposedPorts,
          );
        } catch (err) {
          console.error(
            "[WS] Failed to parse message or process instances:",
            err,
          );
          setCardStatus({});
        }
      },
    );

    stompClient.publish({
      destination: "/skeyegate/instances/subscribe/minimal",
    });
  };

  stompClient.activate();

  return () => disconnectWebSocket();
};

// FIX 2: Added "Content-Type" explicitly to API fetches to prevent 415 HTTP Errors
export const getAllInstances = async (
  setCardStatus,
  setContainerIds,
  toast,
  labelToTitleMap,
  setExposedPorts,
) => {
  try {
    const response = await api.get("/instances", {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    processInstances(
      response.data.data,
      labelToTitleMap,
      setCardStatus,
      setContainerIds,
      setExposedPorts,
    );
  } catch (error) {
    if (toast?.current) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch instances",
      });
    }
    console.error("Error during getAllInstances:", error);
  }
};

// FIX 2: Added "Content-Type" explicitly to API fetches to prevent 415 HTTP Errors
export const getAllInstancesMinimal = async (
  setCardStatus,
  setContainerIds,
  toast,
  labelToTitleMap,
  setExposedPorts,
) => {
  try {
    const response = await api.get("/instances/minimal", {
      headers: {
        Accept: "application/json",
      },
      data: {},
    });
    processInstances(
      response.data.data,
      labelToTitleMap,
      setCardStatus,
      setContainerIds,
      setExposedPorts,
    );
    return true;
  } catch (error) {
    if (toast?.current) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch instances",
      });
    }
    console.error("Error during getAllInstancesMinimal:", error);
    return false;
  }
};

export const streamContainerLogs = async (containerId, signal) => {
  const token = localStorage.getItem("SkeyegateAuthToken");
  const backendIp = window._env_?.REACT_APP_BACKEND_IP || "localhost";
  const backendPort = window._env_?.REACT_APP_BACKEND_PORT || "8080";

  return await fetch(
    `${window.location.protocol}//${backendIp}:${backendPort}/api/instances/logs/${containerId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      signal: signal,
    },
  );
};
